import { Label } from "@radix-ui/react-label";
import { ChevronDown } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CardContainer } from "@/components/card-container";
import { ErrorBox } from "@/components/error-box";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { getCardDescriptionKey } from "@/features/card/utils/card-labels";
import { useAvailablePaymentMethods } from "@/features/payment-methods";
import {
  resetForm,
  selectRequiredPayableType,
  selectSelectedPaymentMethod,
  setSelectedPaymentMethod,
} from "@/features/payment-methods/store/payment-method-form-slice";
import {
  canReceivePayableType,
  usePaymentMethods,
  usePaymentSetupStatus,
} from "@/features/payments";
import {
  canSetDefault,
  type PaymentAccountCard,
} from "@/features/payments/types/payment-account-cards";
import { useAppDispatch, useAppSelector } from "@/store";
import { AppRoute } from "@/types/routes";

import { usePaymentMethodForms } from "../../contexts/use-payment-method-forms";
import {
  getCardTypeForAccountType,
  getCardTypeForPaymentType,
  type PaymentMethod,
  type MethodProviderName,
} from "../../types/available-payment-methods";

import { PaymentMethodDescription } from "./payment-method-description";

export const SelectPaymentMethodPage = () => {
  const { t } = useTranslation();
  const {
    setDefaultPaymentMethod,
    isSettingDefaultPaymentMethod,
    completePaymentMethodChange,
  } = usePaymentMethodForms();
  const dispatch = useAppDispatch();
  const {
    availablePaymentMethods,
    duplicatedPaymentTypes,
    isLoadingAvailablePaymentMethods,
  } = useAvailablePaymentMethods();
  const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
  const requiredPayableType = useAppSelector(selectRequiredPayableType);

  const { cards } = usePaymentMethods();
  // Which rows are offered, hidden or disabled depends on the held cards and
  // the Default Payment Account, so the list waits for those too. Otherwise
  // every method flashes as pickable until the cards arrive.
  const { activePaymentAccount, isLoading: isLoadingPaymentAccounts } =
    usePaymentSetupStatus();
  const isLoading =
    isLoadingAvailablePaymentMethods || isLoadingPaymentAccounts;
  const navigate = useNavigate();

  // On a Claim Detour only methods that can receive the claim are worth
  // choosing. The rest still exist, so they are shown, collapsed and
  // unselectable, rather than silently dropped.

  useEffect(() => {
    dispatch(resetForm());
  }, [dispatch]);

  const handlePaymentMethodChange = (value: string) => {
    dispatch(setSelectedPaymentMethod(value as MethodProviderName));
  };

  /** The card the participant already holds for a card payment method, if any. */
  const findExistingCard = (
    method: PaymentMethod
  ): PaymentAccountCard | undefined => {
    const cardType = getCardTypeForPaymentType(method.payment_type);
    return cardType
      ? cards.find((card) => card.card_type === cardType)
      : undefined;
  };

  const handleCreateNewPaymentAccount = () => {
    if (!selectedPaymentMethod) return;

    const method = availablePaymentMethods.find(
      (candidate) => candidate.method_provider_name === selectedPaymentMethod
    );
    const existingCard = method ? findExistingCard(method) : undefined;

    if (existingCard && canSetDefault(existingCard.card_type)) {
      setDefaultPaymentMethod(
        existingCard.card_type,
        existingCard.provider,
        completePaymentMethodChange
      );
    } else if (method && getCardTypeForPaymentType(method.payment_type)) {
      navigate(AppRoute.CARD_ONBOARDING);
    } else {
      navigate(`/payment-methods/${selectedPaymentMethod}/create`);
    }
  };

  // An existing card is offered as the thing to switch to. Spend Cards spend
  // the sponsor's pool, so they can never be the active payment account and
  // are shown but disabled. Asking `canSetDefault` rather than naming the Card
  // Types keeps the rule true for every Card Type the generated enum admits.
  // `is_read_only` is how PPS flagged Highnote Spend Cards before the card type
  // existed and is kept as a belt-and-braces check.
  const isMethodDisabled = (method: PaymentMethod) => {
    const existingCard = findExistingCard(method);
    return (
      !!existingCard &&
      (!canSetDefault(existingCard.card_type) || existingCard.is_read_only)
    );
  };

  // A held card that already is the Default Payment Account has nothing to
  // switch to, so its row is not offered at all. Bank accounts and debit cards
  // stay listed because choosing them creates a new account to switch to.
  const activeCardType = activePaymentAccount
    ? getCardTypeForAccountType(activePaymentAccount.target_account_type)
    : undefined;
  const isCurrentDefaultCard = (method: PaymentMethod) => {
    const existingCard = findExistingCard(method);
    return !!existingCard && existingCard.card_type === activeCardType;
  };
  const offeredMethods = availablePaymentMethods.filter(
    (method) => !isCurrentDefaultCard(method)
  );

  const isClaimCapable = (method: PaymentMethod) =>
    requiredPayableType === null ||
    canReceivePayableType(method, requiredPayableType);
  const claimIncapableMethods = offeredMethods.filter(
    (method) => !isClaimCapable(method)
  );
  // A held Spend Card can never be the active payment account, so it sits in
  // its own collapsed section rather than as a greyed row among the methods
  // the participant can actually pick.
  const paymentIneligibleMethods = offeredMethods.filter(
    (method) => isClaimCapable(method) && isMethodDisabled(method)
  );
  const selectableMethods = offeredMethods.filter(
    (method) => isClaimCapable(method) && !isMethodDisabled(method)
  );

  const getPaymentTypeLabel = (
    method: PaymentMethod,
    duplicatedPaymentTypes: Record<string, number>
  ) => {
    const existingCard = findExistingCard(method);
    if (existingCard) {
      // A held physical card that is not activated yet has a masked number,
      // and "ending in ****" is kept on purpose: a bare title would read as
      // an offer to create a new card rather than the one already held.
      return t(getCardDescriptionKey(existingCard.card_type), {
        "0": existingCard.card_number.slice(-4) || "****",
      });
    }

    if (duplicatedPaymentTypes[method.payment_type]) {
      return `${method.title} - ${method.provider}`;
    }

    return method.title;
  };

  /** Methods the participant may not pick right now, shown collapsed and disabled. */
  const renderCollapsedMethods = (title: string, methods: PaymentMethod[]) => {
    if (methods.length === 0) return null;
    return (
      <Collapsible className="mt-2">
        <CollapsibleTrigger className="group flex w-full items-center justify-between rounded-lg px-1 py-2 text-sm text-muted-foreground">
          {title}
          <ChevronDown className="size-4 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-y-4 pt-2">
          {methods.map((method) => (
            <div
              key={method.method_provider_name}
              className="flex items-start gap-x-3 rounded-lg border border-input p-4 cursor-not-allowed text-muted-foreground"
            >
              <RadioGroupItem
                id={method.method_provider_name}
                value={method.method_provider_name}
                disabled
                aria-label={getPaymentTypeLabel(method, duplicatedPaymentTypes)}
                className="mt-0.5"
              />
              <span className="capitalize">
                {getPaymentTypeLabel(method, duplicatedPaymentTypes)}
              </span>
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <CardContainer>
      <CardContainer.Header>
        <CardContainer.HeaderContent>
          <CardContainer.CardTitle>
            {t("mileageReview_PaymentMethod")}
          </CardContainer.CardTitle>
          <CardContainer.CardDescription>
            {t("payment_description")}
          </CardContainer.CardDescription>
        </CardContainer.HeaderContent>
      </CardContainer.Header>

      <CardContainer.Content>
        {requiredPayableType !== null && (
          <div className="mb-4">
            <ErrorBox text={t("paymentMethods_claimDetourNotice")} />
          </div>
        )}

        {isLoading ? (
          <div
            className="flex flex-col gap-y-4"
            role="status"
            aria-label="Loading payment methods"
          >
            <div className="flex flex-row gap-x-2">
              <Skeleton className="w-4 h-4 rounded-full" />
              <Skeleton className="w-40 h-4" />
            </div>
            <div className="flex flex-row gap-x-2">
              <Skeleton className="w-4 h-4 rounded-full" />
              <Skeleton className="w-40 h-4" />
            </div>
          </div>
        ) : (
          <fieldset>
            <RadioGroup
              className="md:flex-col"
              aria-label="payment method"
              name="payment-method"
              value={selectedPaymentMethod ?? ""}
              onValueChange={handlePaymentMethodChange}
            >
              {selectableMethods.map((method) => {
                // The description sells creating the method. When the
                // participant already holds the card the row only switches
                // the default, so shipping or availability copy would mislead.
                const description = findExistingCard(method)
                  ? null
                  : method.description;
                const descriptionId = `${method.method_provider_name}-description`;
                const titleId = `${method.method_provider_name}-title`;

                // The whole card is the radio's <label>, so any click on it
                // selects the method. `aria-labelledby` keeps the accessible
                // name to the title alone rather than the label's full text.
                return (
                  <Label
                    key={method.method_provider_name}
                    htmlFor={method.method_provider_name}
                    className="flex items-start gap-x-3 rounded-lg border border-input p-4 transition-colors cursor-pointer hover:border-primary/60 has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5"
                  >
                    <RadioGroupItem
                      id={method.method_provider_name}
                      value={method.method_provider_name}
                      aria-labelledby={titleId}
                      aria-describedby={description ? descriptionId : undefined}
                      className="mt-0.5"
                    />
                    <div className="flex flex-col gap-y-1">
                      <span id={titleId} className="capitalize">
                        {getPaymentTypeLabel(method, duplicatedPaymentTypes)}
                      </span>
                      {description && (
                        <PaymentMethodDescription
                          id={descriptionId}
                          description={description}
                        />
                      )}
                    </div>
                  </Label>
                );
              })}

              {renderCollapsedMethods(
                t("paymentMethods_notAvailableForClaims"),
                claimIncapableMethods
              )}
              {renderCollapsedMethods(
                t("paymentMethods_notAvailableForPayments"),
                paymentIneligibleMethods
              )}
            </RadioGroup>
          </fieldset>
        )}

        <div className="mt-6">
          <Button
            className="w-full"
            size="lg"
            onClick={handleCreateNewPaymentAccount}
            loading={isSettingDefaultPaymentMethod}
            disabled={!selectedPaymentMethod || isSettingDefaultPaymentMethod}
          >
            {t("continue_text")}
          </Button>
        </div>
      </CardContainer.Content>
    </CardContainer>
  );
};
